import React, { useState, useEffect } from 'react';
import AddAchievementModal from '../components/AddAchievementModal';
import CompactAchievementItem from '../components/CompactAchievementItem';
import AchievementDetailsPanel from '../components/AchievementDetailsPanel';
import RequestEndorsementModal from '../components/RequestEndorsementModal';
import RecordStatusBanner from '../components/RecordStatusBanner';
import apiService from '../services/apiService';
import PendingLogsPanel from '../components/PendingLogsPanel';
import NotAnAchievementYetModal from '../components/NotAnAchievementYetModal';

const AchievementsPage = ({
  achievements = [],
  onAddAchievement,
  onEditAchievement,
  onDeleteAchievement,
  onAddWitnesses,
  onRefreshAchievements,
  reviewDate,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAchievement, setEditingAchievement] = useState(null);
  const [endorsementFilter, setEndorsementFilter] = useState('all'); // 'all' | 'verified' | 'needs'
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'Work Achievement' | 'Personal Achievement' | 'Learning Achievement'
  const [selectedAchievement, setSelectedAchievement] = useState(null);
  const [isEndorsementModalOpen, setIsEndorsementModalOpen] = useState(false);
  const [endorsingAchievement, setEndorsingAchievement] = useState(null);
  const [highlightPrompt, setHighlightPrompt] = useState(null); // achievement to prompt endorsement for
  // Mobile: 'list' shows the list panel, 'detail' shows the detail panel
  const [mobileView, setMobileView] = useState('list');

  // "Not an achievement yet?" placeholders — see design-system/Terminology.md
  const [pendingLogs, setPendingLogs] = useState([]);
  const [isPendingModalOpen, setIsPendingModalOpen] = useState(false);
  const [pendingLogDraft, setPendingLogDraft] = useState(null); // { id, note } being converted

  const filteredAchievements = achievements.filter(a => {
    const endorseMatch =
      endorsementFilter === 'all' ? true :
      endorsementFilter === 'verified' ? !!a.endorsed :
      endorsementFilter === 'highlights' ? !!a.is_highlight :
      true;
    const typeMatch = typeFilter === 'all' || a.status === typeFilter;
    return endorseMatch && typeMatch;
  });

  // Auto-select first item when list changes
  useEffect(() => {
    if (filteredAchievements.length > 0) {
      if (!selectedAchievement || !filteredAchievements.find(a => a.id === selectedAchievement.id)) {
        setSelectedAchievement(filteredAchievements[0]);
      }
    } else {
      setSelectedAchievement(null);
    }
  }, [filteredAchievements.length, endorsementFilter, typeFilter]);

  // Keep selectedAchievement in sync with the latest data from the achievements prop
  useEffect(() => {
    if (selectedAchievement) {
      const fresh = achievements.find(a => a.id === selectedAchievement.id);
      if (fresh) setSelectedAchievement(fresh);
    }
  }, [achievements]);

  const handleAddAchievement = () => {
    setEditingAchievement(null);
    setIsModalOpen(true);
  };

  const loadPendingLogs = async () => {
    try {
      const logs = await apiService.getPendingLogs();
      setPendingLogs(logs || []);
    } catch (err) {
      console.error('Failed to load pending logs:', err);
    }
  };

  useEffect(() => { loadPendingLogs(); }, []);

  const handleOpenPendingModal = () => setIsPendingModalOpen(true);

  const handleSavePendingLog = async (note) => {
    const log = await apiService.createPendingLog(note);
    setPendingLogs(prev => [log, ...prev]);
  };

  // Opens the normal Capture-a-win form, pre-filled with the placeholder's note.
  const handleCapturePendingLog = (log) => {
    setEditingAchievement(null);
    setPendingLogDraft(log);
    setIsModalOpen(true);
  };

  const handleDismissPendingLog = async (id) => {
    setPendingLogs(prev => prev.filter(p => p.id !== id));
    try {
      await apiService.deletePendingLog(id);
    } catch (err) {
      console.error('Failed to dismiss pending log:', err);
      loadPendingLogs(); // out of sync with the server — refetch
    }
  };

  const handleEditAchievement = (achievement) => {
    setEditingAchievement(achievement);
    setIsModalOpen(true);
  };

  const handleSaveAchievement = async (formData) => {
    if (editingAchievement) {
      return await onEditAchievement(editingAchievement.id, formData);
    }

    const result = await onAddAchievement(formData);

    // If this achievement started life as a "Not an achievement yet?" placeholder,
    // it's done its job — remove it so it doesn't also linger in the pending strip.
    if (pendingLogDraft) {
      const draftId = pendingLogDraft.id;
      setPendingLogs(prev => prev.filter(p => p.id !== draftId));
      try {
        await apiService.deletePendingLog(draftId);
      } catch (err) {
        console.error('Failed to clean up pending log after conversion:', err);
      }
    }

    return result;
  };

  const handleRequestEndorsement = (achievement) => {
    const target = typeof achievement === 'object'
      ? achievement
      : (achievements || []).find(a => a.id === achievement);
    setEndorsingAchievement(target || null);
    setIsEndorsementModalOpen(true);
  };

  const verifiedCount = achievements.filter(a => !!a.endorsed).length;
  const highlightCount = achievements.filter(a => !!a.is_highlight).length;

  const endorsementTabs = [
    { id: 'all', label: 'All' },
    { id: 'highlights', label: 'Highlights', icon: true },
    { id: 'verified', label: '✓ Verified' },
  ];

  const handleSelectAchievement = (achievement) => {
    setSelectedAchievement(achievement);
    setMobileView('detail');
  };

  const handleToggleHighlight = async (achievement, isHighlight) => {
    try {
      await apiService.toggleHighlight(achievement.id, isHighlight);
      const updatedAchievement = { ...achievement, is_highlight: isHighlight };
      setSelectedAchievement(updatedAchievement);
      // Set prompt BEFORE refresh so the parent re-render doesn't race against it
      if (isHighlight) setHighlightPrompt(updatedAchievement);
      if (onRefreshAchievements) onRefreshAchievements();
    } catch (err) {
      console.error('Failed to toggle highlight:', err);
    }
  };

  // Full-page empty state when user has no achievements at all
  if (achievements.length === 0) {
    return (
      <div className="flex flex-col h-full" data-testid="achievements-page">
        <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 max-w-2xl mx-auto w-full">
          <h1 className="text-2xl font-bold text-gray-900 mb-2 text-center">Your record starts here.</h1>
          <p className="text-gray-500 text-base mb-10 text-center">
            Not sure what counts? More than you think. Here's what's worth logging:
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full mb-10">
            {[
              { emoji: '✅', title: 'Something you finished', desc: 'Shipped a feature, closed a deal, completed a project — big or small.' },
              { emoji: '🔧', title: 'A problem you solved', desc: 'Fixed something broken, found a better way, unblocked a teammate.' },
              { emoji: '⏱️', title: 'Something that saved time or money', desc: 'Improved a process, automated a task, cut something unnecessary.' },
              { emoji: '💬', title: 'Feedback your manager gave you', desc: 'Got a compliment in a 1:1, mentioned in a meeting, praised in Slack.' },
              { emoji: '📚', title: 'Something you learned and applied', desc: 'Picked up a skill and used it on a real project or problem.' },
              { emoji: '🤝', title: 'Something others relied on you for', desc: 'Led a project, mentored someone, became the go-to person for something.' },
            ].map(({ emoji, title, desc }) => (
              <div key={title} className="flex gap-3 bg-white border border-gray-200 rounded-xl p-4">
                <span className="text-xl shrink-0 mt-0.5">{emoji}</span>
                <div>
                  <p className="font-semibold text-sm text-gray-900 mb-0.5">{title}</p>
                  <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3">
            <button
              onClick={handleAddAchievement}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white font-semibold rounded-xl hover:bg-gray-700 transition-colors text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Capture your first win
            </button>
            <button
              onClick={handleOpenPendingModal}
              className="inline-flex items-center gap-2 px-5 py-3 bg-white text-gray-600 font-medium rounded-xl text-sm border border-gray-200 hover:border-gray-300 transition-colors"
            >
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l2 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Not an achievement yet?
            </button>
          </div>

          {pendingLogs.length > 0 && (
            <div className="w-full mt-6">
              <PendingLogsPanel
                pendingLogs={pendingLogs}
                onCapture={handleCapturePendingLog}
                onDismiss={handleDismissPendingLog}
              />
            </div>
          )}
        </div>

        <AddAchievementModal
          isOpen={isModalOpen}
          onClose={() => { setIsModalOpen(false); setEditingAchievement(null); setPendingLogDraft(null); }}
          onSave={handleSaveAchievement}
          achievement={editingAchievement}
          initialDraft={pendingLogDraft ? { description: pendingLogDraft.note } : null}
          onRefreshAchievements={onRefreshAchievements}
        />

        <NotAnAchievementYetModal
          isOpen={isPendingModalOpen}
          onClose={() => setIsPendingModalOpen(false)}
          onSave={handleSavePendingLog}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="achievements-page">

      {/* Page header */}
      <div className="px-4 md:px-8 pt-5 md:pt-7 pb-4 md:pb-5 shrink-0">
        <div className="flex items-center justify-between mb-1 gap-3">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">My Achievements</h1>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={handleOpenPendingModal}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-white text-gray-600 text-sm font-medium rounded-lg border border-gray-200 hover:border-gray-300 transition-colors shrink-0"
            >
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l2 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="hidden sm:inline">Not an achievement yet?</span>
            </button>
            <button
              onClick={handleAddAchievement}
              className="inline-flex items-center gap-1.5 px-3 md:px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors shrink-0"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="hidden sm:inline">Capture a win</span>
              <span className="sm:hidden">Add</span>
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          {achievements.length} logged · {highlightCount} highlight{highlightCount !== 1 ? 's' : ''} · {verifiedCount} verified
        </p>

        {/* Filters */}
        <div className="flex items-center justify-between gap-2 overflow-x-auto pb-1">
          {/* Endorsement pill tabs */}
          <div className="flex gap-1.5 md:gap-2 shrink-0">
            {endorsementTabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setEndorsementFilter(tab.id)}
                className={`inline-flex items-center gap-1.5 px-3 md:px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  endorsementFilter === tab.id
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300 hover:text-gray-900'
                }`}
              >
                {tab.icon && (
                  <svg className={`w-3 h-3 ${endorsementFilter === tab.id ? 'text-white' : 'text-amber-600'}`} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
                  </svg>
                )}
                {tab.label}
              </button>
            ))}
          </div>

          {/* Type dropdown */}
          <select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            className="text-sm text-gray-600 border border-gray-200 rounded-lg px-2 md:px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-400 transition-colors cursor-pointer shrink-0"
          >
            <option value="all">All types</option>
            <option value="Work Achievement">Work</option>
            <option value="Personal Achievement">Personal</option>
            <option value="Learning Achievement">Learning</option>
          </select>
        </div>
      </div>

      <PendingLogsPanel
        pendingLogs={pendingLogs}
        onCapture={handleCapturePendingLog}
        onDismiss={handleDismissPendingLog}
      />

      {/* Record status banner — contextual nudge based on record health */}
      <RecordStatusBanner
        achievements={achievements}
        onRequestEndorsement={handleRequestEndorsement}
        reviewDate={reviewDate}
      />

      {/* Two-panel layout */}
      <div className="flex flex-1 mx-2 md:mx-8 mb-2 md:mb-8 overflow-hidden rounded-xl border border-gray-200 shadow-sm">

        {/* Left: list — full width on mobile (hidden when detail shown), fixed 288px on desktop */}
        <div className={`${mobileView === 'detail' ? 'hidden' : 'flex'} md:flex w-full md:w-72 md:shrink-0 bg-stone-50 md:border-r md:border-gray-200 flex-col overflow-hidden`}>
          <div className="overflow-y-auto flex-1">
            {filteredAchievements.length === 0 ? (
              <div className="text-center px-6 py-12">
                <p className="text-sm font-medium text-gray-700 mb-1">No achievements found</p>
                <p className="text-xs text-gray-400 mb-4">
                  {achievements.length === 0
                    ? 'Add your first achievement to get started.'
                    : 'Try a different filter.'}
                </p>
                {achievements.length === 0 && (
                  <button
                    onClick={handleAddAchievement}
                    className="px-3 py-1.5 bg-gray-900 text-white text-xs font-medium rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Add Achievement
                  </button>
                )}
              </div>
            ) : (
              filteredAchievements.map(achievement => (
                <CompactAchievementItem
                  key={achievement.id}
                  achievement={achievement}
                  isSelected={selectedAchievement?.id === achievement.id}
                  onClick={handleSelectAchievement}
                />
              ))
            )}
          </div>
        </div>

        {/* Right: detail — full width on mobile (hidden when list shown), flex-1 on desktop */}
        <div className={`${mobileView === 'list' ? 'hidden' : 'flex flex-col'} md:flex md:flex-col flex-1 bg-white overflow-y-auto min-w-0`}>
          {/* Back to list button — mobile only */}
          <button
            className="md:hidden flex items-center gap-1.5 px-4 py-3 border-b border-gray-100 text-sm text-gray-500 hover:text-gray-800 transition-colors shrink-0"
            onClick={() => setMobileView('list')}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            All achievements
          </button>
          <div className="flex-1 overflow-y-auto">
            <AchievementDetailsPanel
              achievement={selectedAchievement}
              onEdit={handleEditAchievement}
              onDelete={onDeleteAchievement}
              onRequestEndorsement={handleRequestEndorsement}
              onToggleHighlight={handleToggleHighlight}
              onAddWitnesses={onAddWitnesses}
            />
          </div>
        </div>
      </div>

      {/* Modals */}
      <AddAchievementModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingAchievement(null); setPendingLogDraft(null); }}
        onSave={handleSaveAchievement}
        achievement={editingAchievement}
        initialDraft={pendingLogDraft ? { description: pendingLogDraft.note } : null}
        onRefreshAchievements={onRefreshAchievements}
      />

      <RequestEndorsementModal
        isOpen={isEndorsementModalOpen}
        onClose={() => { setIsEndorsementModalOpen(false); setEndorsingAchievement(null); }}
        achievement={endorsingAchievement}
        onSuccess={() => { if (onRefreshAchievements) onRefreshAchievements(); }}
      />

      <NotAnAchievementYetModal
        isOpen={isPendingModalOpen}
        onClose={() => setIsPendingModalOpen(false)}
        onSave={handleSavePendingLog}
      />

      {/* Highlight endorsement nudge */}
      {highlightPrompt && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setHighlightPrompt(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">Added to your profile!</h3>
            <p className="text-sm text-gray-500 mb-5">
              Want to make it even stronger? Ask your manager to verify it — takes 30 seconds and makes it credible to recruiters.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setHighlightPrompt(null);
                  handleRequestEndorsement(highlightPrompt);
                }}
                className="flex-1 py-2.5 bg-gray-900 text-white text-sm font-semibold rounded-xl hover:bg-gray-700 transition-colors"
              >
                Request Verification
              </button>
              <button
                onClick={() => setHighlightPrompt(null)}
                className="flex-1 py-2.5 bg-gray-100 text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-200 transition-colors"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AchievementsPage;
